import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  Globe,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Shield,
  RefreshCw,
  AlertTriangle,
  ChevronRight,
  HelpCircle,
  Settings,
} from "lucide-react";
import {
  api,
  Domain,
  DkimSelector,
  Source,
} from "@/lib/api";
import { useAuthStore } from "@/stores/auth";
import { toast } from "@/lib/toast";
import { EnforcementTab } from "@/components/domain/EnforcementTab";
import { ConfigurationTab } from "@/components/domain/ConfigurationTab";
import { ShieldCheck } from "lucide-react";

type TabType = "enforcement" | "sources" | "configuration";

// Map old tab IDs to new ones for backwards compatibility
const TAB_REDIRECTS: Record<string, TabType> = {
  overview: "enforcement",
  dkim: "configuration",
  dns: "configuration",
  bimi: "configuration",
  reports: "enforcement",
  tasks: "enforcement",
};

export function DomainDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { organization } = useAuthStore();
  const [domain, setDomain] = useState<Domain | null>(null);
  const [sources, setSources] = useState<Source[]>([]);
  const [dkimSelectors, setDkimSelectors] = useState<DkimSelector[]>([]);

  // Get initial tab from URL query param, default to enforcement
  const tabFromUrl = searchParams.get("tab");
  const validTabs: TabType[] = ["enforcement", "sources", "configuration"];
  const resolvedTab: TabType =
    tabFromUrl && validTabs.includes(tabFromUrl as TabType)
      ? (tabFromUrl as TabType)
      : tabFromUrl && TAB_REDIRECTS[tabFromUrl]
        ? TAB_REDIRECTS[tabFromUrl]
        : "enforcement";
  const [activeTab, setActiveTab] = useState<TabType>(resolvedTab);
  const [loading, setLoading] = useState(true);

  // Sync activeTab when URL search params change (e.g. browser back/forward)
  useEffect(() => {
    const currentTab = searchParams.get("tab");
    const resolved: TabType =
      currentTab && validTabs.includes(currentTab as TabType)
        ? (currentTab as TabType)
        : currentTab && TAB_REDIRECTS[currentTab]
          ? TAB_REDIRECTS[currentTab]
          : "enforcement";
    setActiveTab(resolved);
    // Fix up legacy tab aliases in the URL
    if (currentTab && TAB_REDIRECTS[currentTab]) {
      setSearchParams({ tab: resolved }, { replace: true });
    }
  }, [searchParams]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        const result = await api.getDomainDetails(id!);
        if (cancelled) return;
        setDomain(result.domain);
        setSources(result.sources);
        setDkimSelectors(result.dkimSelectors);
      } catch (error) {
        if (!cancelled) console.error("Failed to fetch domain:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [id]);

  async function fetchDomainData() {
    try {
      setLoading(true);
      const result = await api.getDomainDetails(id!);
      setDomain(result.domain);
      setSources(result.sources);
      setDkimSelectors(result.dkimSelectors);
    } catch (error) {
      console.error("Failed to fetch domain:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyDomain() {
    if (!id) return;
    try {
      await api.verifyDomain(id);
      fetchDomainData();
    } catch (error) {
      console.error("Failed to verify domain:", error);
      toast.error("Failed to verify domain");
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!domain) {
    return (
      <div className="text-center py-12">
        <Globe className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2">
          Domain not found
        </h2>
        <p className="text-slate-500 mb-4">
          The domain you're looking for doesn't exist.
        </p>
        <Link
          to="/domains"
          className="text-amber-600 hover:text-amber-700 font-medium"
        >
          Back to Domains
        </Link>
      </div>
    );
  }

  const tier = organization?.billing_tier || "free";

  const tabs: { id: TabType; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: "enforcement", label: "Enforcement", icon: ShieldCheck },
    { id: "sources", label: "Sources", icon: Shield },
    { id: "configuration", label: "Configuration", icon: Settings },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/domains")}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-800">{domain.domain_name}</h1>
            {domain.verification_status === "verified" ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                <CheckCircle2 className="w-4 h-4" />
                Verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-sm">
                <AlertTriangle className="w-4 h-4" />
                Pending Verification
              </span>
            )}
          </div>
          <p className="text-slate-500">
            Added {new Date(domain.created_at).toLocaleDateString()}
          </p>
        </div>
        {domain.verification_status !== "verified" && (
          <button
            onClick={handleVerifyDomain}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
            Verify Domain
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 -mx-4 px-4 md:mx-0 md:px-0">
        <nav className="flex gap-4 lg:gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSearchParams({ tab: tab.id }, { replace: true });
              }}
              className={`flex items-center gap-2 px-3 py-3 border-b-2 transition-colors whitespace-nowrap text-sm ${
                activeTab === tab.id
                  ? "border-amber-500 text-amber-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <tab.icon className="w-4 h-4 flex-shrink-0" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "enforcement" && (
        <EnforcementTab domainId={domain.id} domainName={domain.domain_name} tier={tier} />
      )}
      {activeTab === "sources" && <SourcesTab sources={sources} domainId={id!} />}
      {activeTab === "configuration" && (
        <ConfigurationTab
          domain={domain}
          dkimSelectors={dkimSelectors}
          domainId={domain.id}
          tier={tier}
          onRefresh={fetchDomainData}
        />
      )}
    </div>
  );
}

// Helper to determine source status - consistent with Sources page
function getSourceStatus(source: Source): "authorized" | "unauthorized" | "pending_review" | "suspicious" {
  // First check if already labeled - labeled sources use their authorization_status
  if (source.authorization_status === "authorized") {
    return "authorized";
  }
  if (source.authorization_status === "unauthorized") {
    return "unauthorized";
  }

  // Not labeled yet - check if it's an unknown IP (suspicious) or known source (pending review)
  const hasHostname = !!source.ptr_hostname;
  const hasKnownProvider = !!(source.org_name || source.provider_name || source.source_name);
  if (!hasHostname && !hasKnownProvider) {
    return "suspicious";
  }

  return "pending_review";
}

// Status badge config for consistent display
const statusConfig = {
  authorized: { label: "Authorized", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  unauthorized: { label: "Unauthorized", color: "bg-red-100 text-red-700", icon: XCircle },
  pending_review: { label: "Pending Review", color: "bg-orange-100 text-orange-700", icon: AlertTriangle },
  suspicious: { label: "Suspicious", color: "bg-purple-100 text-purple-600", icon: HelpCircle },
};

/**
 * SourcesTab - Streamlined summary view that links to the main Sending Sources page
 *
 * This replaces the previous duplicated sources table with a quick summary
 * and a prominent link to the full Sources page (pre-filtered by domain).
 * All source management (labeling, bulk actions, etc.) is centralized in /sources.
 */
function SourcesTab({
  sources,
  domainId,
}: {
  sources: Source[];
  domainId: string;
}) {
  const navigate = useNavigate();

  // Calculate summary stats
  const statusCounts = useMemo(() => ({
    total: sources.length,
    authorized: sources.filter((s) => getSourceStatus(s) === "authorized").length,
    unauthorized: sources.filter((s) => getSourceStatus(s) === "unauthorized").length,
    pending_review: sources.filter((s) => getSourceStatus(s) === "pending_review").length,
    suspicious: sources.filter((s) => getSourceStatus(s) === "suspicious").length,
  }), [sources]);

  // Calculate total messages
  const totalMessages = sources.reduce((sum, s) => sum + (s.total_message_count || 0), 0);

  // Top sources by volume (preview)
  const topSources = useMemo(() =>
    [...sources]
      .sort((a, b) => (b.total_message_count || 0) - (a.total_message_count || 0))
      .slice(0, 5),
    [sources]
  );

  if (sources.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
        <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-800 mb-2">
          No sources detected yet
        </h3>
        <p className="text-slate-500">
          Sources will appear here once DMARC reports are received for this domain.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-slate-800">{statusCounts.total}</p>
          <p className="text-xs text-slate-500">Total Sources</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-emerald-700">{statusCounts.authorized}</p>
          <p className="text-xs text-slate-500">Authorized</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-orange-600">{statusCounts.pending_review}</p>
          <p className="text-xs text-slate-500">Pending Review</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{statusCounts.unauthorized}</p>
          <p className="text-xs text-slate-500">Unauthorized</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{statusCounts.suspicious}</p>
          <p className="text-xs text-slate-500">Suspicious</p>
        </div>
      </div>

      {/* Top Sources Preview */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-700">Top Sources by Volume</h3>
          <span className="text-xs text-slate-500">{totalMessages.toLocaleString()} total messages</span>
        </div>
        <div className="space-y-2">
          {topSources.map((source) => {
            const status = getSourceStatus(source);
            const config = statusConfig[status];
            const Icon = config.icon;
            const displayName = source.source_name || source.provider_name || source.ptr_hostname || source.source_ip;
            return (
              <div key={source.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-3 min-w-0">
                  <Icon className={`w-4 h-4 flex-shrink-0 ${status === 'authorized' ? 'text-emerald-700' : status === 'unauthorized' ? 'text-red-600' : status === 'suspicious' ? 'text-purple-600' : 'text-orange-600'}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{displayName}</p>
                    {source.org_name && source.org_name !== displayName && (
                      <p className="text-xs text-slate-500 truncate">{source.org_name}</p>
                    )}
                  </div>
                </div>
                <span className="text-sm text-slate-600 flex-shrink-0 ml-3">
                  {(source.total_message_count || 0).toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Link to Full Sources Page */}
      <div className="bg-amber-50 rounded-xl border border-amber-200 p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-1">Manage Sending Sources</h3>
            <p className="text-sm text-slate-600">
              Label sources, apply bulk actions, and view detailed authentication metrics on the Sending Sources page.
            </p>
          </div>
          <button
            onClick={() => navigate(`/sources?domain=${domainId}`)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors whitespace-nowrap"
          >
            <Shield className="w-4 h-4" />
            View All Sources
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
